import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import type { Tenant, TenantCreateInput, TenantUpdateInput } from '@/types';
import { logEvent } from './audit.service';

const COL = 'tenants';

function fromFirestore(id: string, data: Record<string, unknown>): Tenant {
  return {
    ...data,
    id,
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate().toISOString() : String(data.createdAt ?? ''),
    updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate().toISOString() : String(data.updatedAt ?? ''),
  } as Tenant;
}

export const tenantService = {
  async getAll(): Promise<Tenant[]> {
    const snap = await getDocs(query(collection(db, COL), orderBy('name')));
    return snap.docs.map((d) => fromFirestore(d.id, d.data()));
  },

  async getById(id: string): Promise<Tenant | null> {
    const snap = await getDoc(doc(db, COL, id));
    if (!snap.exists()) return null;
    return fromFirestore(snap.id, snap.data());
  },

  async getBySlug(slug: string): Promise<Tenant | null> {
    const q = query(collection(db, COL), where('slug', '==', slug));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0];
    return fromFirestore(d.id, d.data());
  },

  async isSlugAvailable(slug: string, ignoreId?: string): Promise<boolean> {
    const q = query(collection(db, COL), where('slug', '==', slug));
    const snap = await getDocs(q);
    if (snap.empty) return true;
    if (ignoreId && snap.docs.length === 1 && snap.docs[0].id === ignoreId) return true;
    return false;
  },

  async create(input: TenantCreateInput, performedBy: string): Promise<Tenant> {
    const now = serverTimestamp();
    const ref = await addDoc(collection(db, COL), {
      ...input,
      createdAt: now,
      updatedAt: now,
      createdBy: performedBy,
      updatedBy: performedBy,
    });
    const created = await this.getById(ref.id);
    await logEvent({
      entityType: 'tenant',
      entityId: ref.id,
      action: 'create',
      tenantId: ref.id,
      performedBy,
      newValue: input as Record<string, unknown>,
    });
    return created!;
  },

  async update(id: string, input: TenantUpdateInput, performedBy: string): Promise<void> {
    const docRef = doc(db, COL, id);
    await updateDoc(docRef, {
      ...input,
      updatedAt: serverTimestamp(),
      updatedBy: performedBy,
    });
    await logEvent({
      entityType: 'tenant',
      entityId: id,
      action: 'update',
      tenantId: id,
      performedBy,
      newValue: input as Record<string, unknown>,
    });
  },

  async delete(id: string, performedBy: string): Promise<void> {
    const docRef = doc(db, COL, id);
    await updateDoc(docRef, {
      status: 'inactive',
      updatedAt: serverTimestamp(),
      updatedBy: performedBy,
    });
    await logEvent({
      entityType: 'tenant',
      entityId: id,
      action: 'update',
      tenantId: id,
      performedBy,
      newValue: { status: 'inactive' },
    });
  },

  async uploadBranding(
    tenantId: string,
    file: File,
    uploadedBy: string,
  ): Promise<{ downloadUrl: string; storagePath: string }> {
    const ext = (file.name.split('.').pop() ?? 'png').toLowerCase();
    const MIME: Record<string, string> = {
      jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
      gif: 'image/gif', webp: 'image/webp', svg: 'image/svg+xml',
    };
    const contentType = file.type || MIME[ext] || 'image/png';
    const fileName = `logo_${Date.now()}.${ext}`;
    const storagePath = `tenants/${tenantId}/branding/${fileName}`;
    const storageRef = ref(storage, storagePath);
    const task = uploadBytesResumable(storageRef, file, {
      contentType,
      customMetadata: { tenantId, uploadedBy },
    });
    await new Promise<void>((resolve, reject) => {
      task.on('state_changed', undefined, reject, resolve);
    });
    const downloadUrl = await getDownloadURL(task.snapshot.ref);
    return { downloadUrl, storagePath };
  },
};
